/** Assinatura da loja com a plataforma: situação, combinado e pagamentos.
 *
 * Controle manual. O valor se combina com a loja, o dinheiro chega por PIX ou
 * transferência, e quem administra a plataforma registra aqui. Atraso não
 * bloqueia nada sozinho: suspender é o "desativar" da loja, decisão humana.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { mensagemDeErro } from '../api/client';
import {
  ApiPlataforma,
  type AssinaturaDetalhe,
  type AssinaturaEntrada,
  type FormaPagamentoAssinatura,
  type PagamentoEntrada,
  type ResumoAssinatura,
  type SituacaoAssinatura,
} from '../api/plataforma';
import { CampoData, CampoMes, CampoMoeda, Card, Carregando, Erro, Linha, Modal, Selo, Vazio } from './base';
import { Icone, type NomeIcone } from './Icone';
import { competencia, data, dataHora, hojeISO, moeda, paraNumero } from '../utils/formato';

const ROTULO: Record<SituacaoAssinatura, string> = {
  cortesia: 'Cortesia',
  teste: 'Em teste',
  a_combinar: 'A combinar',
  em_dia: 'Em dia',
  em_atraso: 'Em atraso',
  suspensa: 'Suspensa',
};

const TIPO_SELO: Record<SituacaoAssinatura, string> = {
  cortesia: 'roxo',
  teste: 'azul',
  a_combinar: 'neutro',
  em_dia: 'verde',
  em_atraso: 'vermelho',
  suspensa: 'neutro',
};

const FORMAS: { valor: FormaPagamentoAssinatura; rotulo: string }[] = [
  { valor: 'pix', rotulo: 'PIX' },
  { valor: 'transferencia', rotulo: 'Transferência' },
  { valor: 'boleto', rotulo: 'Boleto' },
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'outro', rotulo: 'Outro' },
];

const rotuloForma = (forma: FormaPagamentoAssinatura) => FORMAS.find((f) => f.valor === forma)?.rotulo ?? forma;

export function SeloAssinatura({ assinatura }: { assinatura: ResumoAssinatura | null }) {
  if (!assinatura) return <span className="texto-nota">—</span>;
  return <Selo tipo={TIPO_SELO[assinatura.situacao]}>{ROTULO[assinatura.situacao]}</Selo>;
}

/** A linha de baixo do selo: o que importa saber de relance em cada situação. */
export function DetalheDaAssinatura({ assinatura }: { assinatura: ResumoAssinatura | null }) {
  return <div className="texto-nota">{textoDoDetalhe(assinatura)}</div>;
}

function textoDoDetalhe(a: ResumoAssinatura | null): string {
  if (!a) return '';
  switch (a.situacao) {
    case 'teste':
      return a.teste_ate ? `até ${data(a.teste_ate)}` : '';
    case 'em_atraso':
      return `desde ${data(a.em_atraso_desde)} · ${moeda(a.valor_em_aberto)}`;
    case 'em_dia':
      return a.proximo_vencimento ? `vence ${data(a.proximo_vencimento)}` : '';
    case 'a_combinar':
      return 'sem valor combinado';
    default:
      return '';
  }
}

/** A faixa do site não bate com o valor combinado — vale um olhar. */
function foraDaFaixa(a: ResumoAssinatura): boolean {
  if (a.cortesia || a.valor === null || a.valor_sugerido === null) return false;
  return paraNumero(a.valor) !== paraNumero(a.valor_sugerido);
}

// ═════════════════════════════════════════════════════════════════════════════
// Cartão da tela da loja (plataforma)
// ═════════════════════════════════════════════════════════════════════════════
export function CartaoAssinatura({ lojaId }: { lojaId: number }) {
  const clienteQuery = useQueryClient();
  const [combinando, setCombinando] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [excluindo, setExcluindo] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['plataforma-assinatura', lojaId],
    queryFn: () => ApiPlataforma.assinatura(lojaId),
  });

  const atualizar = (nova: AssinaturaDetalhe) => {
    clienteQuery.setQueryData(['plataforma-assinatura', lojaId], nova);
    void clienteQuery.invalidateQueries({ queryKey: ['plataforma-lojas'] });
    void clienteQuery.invalidateQueries({ queryKey: ['plataforma-cobranca'] });
    void clienteQuery.invalidateQueries({ queryKey: ['plataforma-loja', lojaId] });
  };

  const excluir = useMutation({
    mutationFn: (pagamentoId: number) => ApiPlataforma.excluirPagamento(lojaId, pagamentoId),
    onSuccess: (nova) => {
      setExcluindo(null);
      atualizar(nova);
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  if (consulta.isLoading) return <Carregando altura={160} />;
  if (consulta.error || !consulta.data) {
    return <Erro mensagem={mensagemDeErro(consulta.error)} aoTentarNovamente={() => consulta.refetch()} />;
  }
  const a = consulta.data;

  return (
    <Card
      titulo={
        <>
          Assinatura <SeloAssinatura assinatura={a} />
        </>
      }
      acao={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn secundario pequeno" onClick={() => setCombinando(true)}>
            <Icone nome="pena" tamanho={14} />
            Combinar
          </button>
          <button
            type="button"
            className="btn pequeno"
            onClick={() => setRegistrando(true)}
            disabled={a.cortesia || a.valor === null}
            title={a.valor === null ? 'Combine o valor antes de registrar pagamento' : undefined}
          >
            <Icone nome="mais" tamanho={14} />
            Registrar pagamento
          </button>
        </div>
      }
    >
      {a.situacao === 'em_atraso' && (
        <div className="aviso erro">
          {a.meses_em_aberto === 1 ? '1 mês em aberto' : `${a.meses_em_aberto} meses em aberto`} — vencido desde{' '}
          {data(a.em_atraso_desde)}, {moeda(a.valor_em_aberto)} ao todo. Nada é bloqueado sozinho: se for o caso,
          desative a loja em <strong>Editar loja</strong>.
        </div>
      )}
      {foraDaFaixa(a) && (
        <div className="aviso atencao">
          O quadro tem {a.irmaos_na_conta} irmão(s) ativo(s) na conta — faixa {a.faixa_sugerida}, de{' '}
          {moeda(a.valor_sugerido)}. O combinado é {moeda(a.valor)}.
        </div>
      )}

      <div className="grade-2">
        <div>
          <Linha rotulo="Mensalidade">
            {a.cortesia ? 'Cortesia, sem cobrança' : a.valor === null ? 'A combinar' : `${moeda(a.valor)} / mês`}
          </Linha>
          <Linha rotulo="Vencimento">{a.dia_vencimento ? `todo dia ${a.dia_vencimento}` : 'dia 10 (padrão)'}</Linha>
          <Linha rotulo="Teste">{a.teste_ate ? `até ${data(a.teste_ate)}` : 'sem teste'}</Linha>
          <Linha rotulo="Cobrança a partir de">{a.primeira_competencia ? competencia(a.primeira_competencia) : '—'}</Linha>
        </div>
        <div>
          <Linha rotulo="Quadro na conta">
            {a.irmaos_na_conta} irmão(s) · {a.faixa_sugerida}
            {a.valor_sugerido ? ` · ${moeda(a.valor_sugerido)}` : ' · sob consulta'}
          </Linha>
          <Linha rotulo="Pago até">{a.pago_ate ? competencia(a.pago_ate) : 'nada pago ainda'}</Linha>
          <Linha rotulo="Próximo vencimento">{a.proximo_vencimento ? data(a.proximo_vencimento) : '—'}</Linha>
          <Linha rotulo="Em aberto" cor={a.meses_em_aberto ? 'var(--rd)' : undefined}>
            {a.meses_em_aberto ? `${moeda(a.valor_em_aberto)} (${a.competencias_em_aberto.map(competencia).join(', ')})` : 'nada'}
          </Linha>
        </div>
      </div>
      {a.observacao && <p className="texto-nota" style={{ marginTop: 8, whiteSpace: 'pre-line' }}>{a.observacao}</p>}

      <div className="sep" />
      {erro && <div className="aviso erro">{erro}</div>}
      {a.pagamentos.length === 0 ? (
        <Vazio>Nenhum pagamento registrado.</Vazio>
      ) : (
        <div className="tbl-wrap">
          <table className="cabe-no-celular">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Competência</th>
                <th className="r" style={{ width: 110 }}>
                  Valor
                </th>
                <th className="esconde-mobile" style={{ width: 110 }}>Pago em</th>
                <th className="esconde-mobile" style={{ width: 120 }}>Forma</th>
                <th className="esconde-mobile" style={{ minWidth: 200 }}>
                  Registro
                </th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {a.pagamentos.map((p) => (
                <tr key={p.id}>
                  <td>
                    {competencia(p.competencia)}
                    <div className="so-mobile texto-nota">
                      {data(p.pago_em)} · {rotuloForma(p.forma)}
                    </div>
                  </td>
                  <td className="r num">{moeda(p.valor)}</td>
                  <td className="esconde-mobile">{data(p.pago_em)}</td>
                  <td className="esconde-mobile">{rotuloForma(p.forma)}</td>
                  <td className="esconde-mobile" style={{ whiteSpace: 'normal' }}>
                    <div className="texto-nota">
                      {p.registrado_por} · {dataHora(p.criado_em)}
                    </div>
                    {p.observacao && <div>{p.observacao}</div>}
                  </td>
                  <td className="r">
                    {excluindo === p.id ? (
                      <button
                        type="button"
                        className="btn perigo pequeno"
                        onClick={() => excluir.mutate(p.id)}
                        disabled={excluir.isPending}
                      >
                        Confirmar exclusão
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn secundario pequeno"
                        onClick={() => {
                          setErro(null);
                          setExcluindo(p.id);
                        }}
                        title="Excluir o pagamento — para corrigir um lançamento errado"
                      >
                        <Icone nome="lixeira" tamanho={14} />
                        Excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal aberto={combinando} aoFechar={() => setCombinando(false)} larguraMaxima={640}>
        {combinando && (
          <FormularioCombinado
            lojaId={lojaId}
            assinatura={a}
            aoFechar={() => setCombinando(false)}
            aoSalvar={(nova) => {
              atualizar(nova);
              setCombinando(false);
            }}
          />
        )}
      </Modal>
      <Modal aberto={registrando} aoFechar={() => setRegistrando(false)} larguraMaxima={560}>
        {registrando && (
          <FormularioPagamento
            lojaId={lojaId}
            // A mais antiga em aberto; sem atraso, a do próximo vencimento.
            competencia={(a.competencias_em_aberto[0] ?? a.proximo_vencimento ?? hojeISO()).slice(0, 7)}
            valor={a.valor}
            aoFechar={() => setRegistrando(false)}
            aoSalvar={(nova) => {
              atualizar(nova);
              setRegistrando(false);
            }}
          />
        )}
      </Modal>
    </Card>
  );
}

function Cabecalho({
  titulo,
  subtitulo,
  icone,
  aoFechar,
}: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  icone: NomeIcone;
  aoFechar: () => void;
}) {
  return (
    <div className="m-head">
      <div className="m-av">
        <Icone nome={icone} tamanho={22} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="m-nome">{titulo}</div>
        {subtitulo && <div className="m-sub">{subtitulo}</div>}
      </div>
      <button type="button" className="m-close" onClick={aoFechar} aria-label="Fechar">
        <Icone nome="fechar" tamanho={18} />
      </button>
    </div>
  );
}

// ── O combinado ─────────────────────────────────────────────────────────────
function FormularioCombinado({
  lojaId,
  assinatura,
  aoFechar,
  aoSalvar,
}: {
  lojaId: number;
  assinatura: AssinaturaDetalhe;
  aoFechar: () => void;
  aoSalvar: (nova: AssinaturaDetalhe) => void;
}) {
  const [form, setForm] = useState<AssinaturaEntrada>({
    valor: assinatura.valor ?? assinatura.valor_sugerido,
    dia_vencimento: assinatura.dia_vencimento ?? 10,
    teste_ate: assinatura.teste_ate,
    inicio: assinatura.inicio,
    cortesia: assinatura.cortesia,
    observacao: assinatura.observacao,
  });
  const [erro, setErro] = useState<string | null>(null);
  const alterar = <K extends keyof AssinaturaEntrada>(campo: K, valor: AssinaturaEntrada[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const salvar = useMutation({
    mutationFn: () =>
      ApiPlataforma.definirAssinatura(lojaId, {
        ...form,
        valor: form.valor ? form.valor.replace(',', '.') : null,
      }),
    onSuccess: aoSalvar,
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <>
      <Cabecalho titulo="Combinar a assinatura" subtitulo="Valor, vencimento e período de teste" icone="carteira" aoFechar={aoFechar} />
      <form
        className="m-body"
        onSubmit={(e) => {
          e.preventDefault();
          setErro(null);
          salvar.mutate();
        }}
      >
        <label className="checa" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={form.cortesia} onChange={(e) => alterar('cortesia', e.target.checked)} />
          <span>
            <strong>Cortesia</strong>
            <div className="texto-nota">A loja não paga — a piloto, uma parceria combinada.</div>
          </span>
        </label>

        <div className="grade-form">
          <div className="campo">
            <label htmlFor="as-valor">Mensalidade</label>
            <CampoMoeda
              id="as-valor"
              valor={form.valor}
              aoMudar={(v) => alterar('valor', v || null)}
              desabilitado={form.cortesia}
            />
            <div className="texto-nota">
              Pelo quadro ({assinatura.irmaos_na_conta} na conta): {assinatura.faixa_sugerida}
              {assinatura.valor_sugerido ? `, ${moeda(assinatura.valor_sugerido)}` : ', sob consulta'}.
            </div>
          </div>
          <div className="campo">
            <label htmlFor="as-dia">Dia do vencimento</label>
            <select
              id="as-dia"
              value={form.dia_vencimento ?? 10}
              onChange={(e) => alterar('dia_vencimento', Number(e.target.value))}
              disabled={form.cortesia}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  dia {d}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="as-teste">Teste até</label>
            <CampoData
              id="as-teste"
              valor={form.teste_ate ?? ''}
              aoMudar={(v) => alterar('teste_ate', v || null)}
            />
            <div className="texto-nota">Até esse dia nada é devido.</div>
          </div>
          <div className="campo">
            <label htmlFor="as-inicio">Cobrar a partir de</label>
            <CampoData
              id="as-inicio"
              valor={form.inicio ?? ''}
              aoMudar={(v) => alterar('inicio', v || null)}
            />
            <div className="texto-nota">Vazio: o dia seguinte ao fim do teste.</div>
          </div>
          <div className="campo col-2">
            <label htmlFor="as-obs">Observação</label>
            <textarea
              id="as-obs"
              rows={3}
              value={form.observacao ?? ''}
              onChange={(e) => alterar('observacao', e.target.value || null)}
              placeholder="Combinado com o Venerável em …, desconto de …"
            />
          </div>
        </div>

        {erro && (
          <div className="aviso erro" style={{ marginTop: 12 }}>
            {erro}
          </div>
        )}
        <div className="form-acoes">
          <button type="button" className="btn secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="btn" disabled={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </>
  );
}

// ── Registrar pagamento ─────────────────────────────────────────────────────
export function FormularioPagamento({
  lojaId,
  competencia,
  valor,
  nomeDaLoja,
  aoFechar,
  aoSalvar,
}: {
  lojaId: number;
  /** Mês já escolhido, em `aaaa-mm`. */
  competencia: string;
  /** O combinado, para vir preenchido. */
  valor: string | null;
  nomeDaLoja?: string;
  aoFechar: () => void;
  aoSalvar: (nova: AssinaturaDetalhe) => void;
}) {
  const [form, setForm] = useState<PagamentoEntrada>({
    competencia,
    valor: valor ?? '',
    pago_em: hojeISO(),
    forma: 'pix',
    observacao: null,
  });
  const [erro, setErro] = useState<string | null>(null);
  const alterar = <K extends keyof PagamentoEntrada>(campo: K, valor: PagamentoEntrada[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const salvar = useMutation({
    mutationFn: () =>
      ApiPlataforma.registrarPagamento(lojaId, {
        ...form,
        competencia: `${form.competencia}-01`,
        valor: form.valor.replace(',', '.'),
      }),
    onSuccess: aoSalvar,
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <>
      <Cabecalho
        titulo="Registrar pagamento"
        subtitulo={nomeDaLoja ?? 'O que entrou por PIX ou transferência'}
        icone="recibo"
        aoFechar={aoFechar}
      />
      <form
        className="m-body"
        onSubmit={(e) => {
          e.preventDefault();
          setErro(null);
          if (!form.competencia || !form.valor || !form.pago_em) {
            setErro('Preencha a competência, o valor e a data do pagamento.');
            return;
          }
          salvar.mutate();
        }}
      >
        <div className="grade-form">
          <div className="campo">
            <label htmlFor="pg-comp">Competência *</label>
            <CampoMes id="pg-comp" valor={form.competencia} aoMudar={(v) => alterar('competencia', v)} obrigatorio />
          </div>
          <div className="campo">
            <label htmlFor="pg-valor">Valor *</label>
            <CampoMoeda id="pg-valor" valor={form.valor} aoMudar={(v) => alterar('valor', v)} obrigatorio />
          </div>
          <div className="campo">
            <label htmlFor="pg-data">Pago em *</label>
            <CampoData id="pg-data" valor={form.pago_em} aoMudar={(v) => alterar('pago_em', v)} obrigatorio />
          </div>
          <div className="campo">
            <label htmlFor="pg-forma">Forma</label>
            <select
              id="pg-forma"
              value={form.forma}
              onChange={(e) => alterar('forma', e.target.value as FormaPagamentoAssinatura)}
            >
              {FORMAS.map((f) => (
                <option key={f.valor} value={f.valor}>
                  {f.rotulo}
                </option>
              ))}
            </select>
          </div>
          <div className="campo col-2">
            <label htmlFor="pg-obs">Observação</label>
            <input
              id="pg-obs"
              value={form.observacao ?? ''}
              onChange={(e) => alterar('observacao', e.target.value || null)}
              placeholder="Ex.: PIX do Tesoureiro, comprovante no e-mail"
            />
          </div>
        </div>

        {erro && (
          <div className="aviso erro" style={{ marginTop: 12 }}>
            {erro}
          </div>
        )}
        <div className="form-acoes">
          <button type="button" className="btn secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="btn" disabled={salvar.isPending}>
            {salvar.isPending ? 'Registrando…' : 'Registrar'}
          </button>
        </div>
      </form>
    </>
  );
}
